'use client';

import HyperDX from '@hyperdx/browser';
import * as Sentry from '@sentry/nextjs';
import { SplashScreen } from '@zolo/ui';
import { useCustomizedUser } from '~/state/hooks/use-session';

interface AuthGuardProps {
  children: JSX.Element;
}

import { createContext, useContext, useEffect } from 'react';
import { hyperDXConfig } from '~/config';
import { UserOrg, UserPermission, UserProfile } from '~/lib/drizzle/type';
import { Modules, PermissionLevel } from '~/lib/drizzle/user_permission';
import { useGetOrganisation } from '~/state/api/v2';
import { useGetUserPermissions } from '~/state/api/v2/user_permission';
import { useGetUserProfile } from '~/state/api/v2/users';

interface UserOrgContextType {
  organization: UserOrg | null;
  profile: UserProfile | null;
  permissions: Partial<UserPermission>[];
  isLoading: boolean;
  error: Error | null;
}

const UserOrgContext = createContext<UserOrgContextType>({
  organization: null,
  profile: null,
  permissions: [],
  isLoading: false,
  error: null,
});

export const useUserOrg = () => useContext(UserOrgContext);

export const hasPermission = (module: Modules, permission: PermissionLevel): boolean => {
  const { profile, permissions } = useUserOrg();
  Sentry.setUser({ id: profile?.id, email: profile?.email ?? undefined });

  if (profile?.user_type === 'OWNER') return true;
  return !!permissions.find((ua) => ua.module === module && ua.permissions?.includes(permission));
};

const UserOrgProvider = ({ children }: { children: React.ReactNode }) => {
  const { data: profile, isLoading: profileLoading, error: profileError } = useGetUserProfile();
  const { data: permissions, isLoading: permissionsLoading, error: permissionsError } = useGetUserPermissions();
  const { data: organisation, isLoading: organisationLoading, error: organisationError } = useGetOrganisation();

  useEffect(() => {
    if (profile && hyperDXConfig.apiKey) {
      HyperDX.setGlobalAttributes({
        userId: profile.id,
        userEmail: profile.email ?? profile.phone ?? 'Unknown',
        orgId: profile.org_id,
      });
    }
  }, [profile, hyperDXConfig.apiKey]);

  return (
    <UserOrgContext.Provider
      value={{
        organization: organisation || null,
        profile: profile || null,
        permissions: permissions || [],
        isLoading: profileLoading || permissionsLoading || organisationLoading,
        error: (profileError as Error) || (organisationError as Error) || (permissionsError as Error) || null,
      }}
    >
      {children}
    </UserOrgContext.Provider>
  );
};

export { UserOrgProvider };

export function AuthGuard(props: AuthGuardProps): JSX.Element | null {
  const { isLoaded, isSignedIn } = useCustomizedUser();

  if (!isLoaded) {
    return <SplashScreen />;
  }

  if (!isSignedIn) {
    window && window.location.replace('/auth/login');
    return null;
  }

  return props.children;
}
